import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

/** Voľba témy; `system` sleduje nastavenie operačného systému. */
export type ThemePreference = 'light' | 'dark' | 'system'

/**
 * Režim zobrazenia cien.
 *
 * `ZAKAZNICKE` skryje rabat, nákupnú cenu a maržu — partner ukazuje obrazovku
 * koncovému zákazníkovi (MASTER.md §8.5). Hodnoty sú dátové, preto po slovensky
 * a veľkými, rovnako ako enumy v doméne.
 */
export type PriceMode = 'INTERNE' | 'ZAKAZNICKE'

const STORAGE_KEY = 'maxdoors.ui'

interface PersistedUiState {
	sidebarCollapsed: boolean
	theme: ThemePreference
	priceMode: PriceMode
}

function readPersisted(): Partial<PersistedUiState> {
	try {
		const raw = globalThis.localStorage?.getItem(STORAGE_KEY)

		return raw === null || raw === undefined ? {} : (JSON.parse(raw) as Partial<PersistedUiState>)
	} catch {
		// Poškodený alebo nedostupný storage nesmie zhodiť aplikáciu.
		return {}
	}
}

/**
 * Stav shellu, ktorý prežije reload.
 *
 * Režim cien je zámerne per zariadenie: notebook v predajni má iné nastavenie
 * ako notebook v kancelárii toho istého človeka (handoff.md, riziko č. 4).
 */
export const useUiStore = defineStore('ui', () => {
	const persisted = readPersisted()

	const sidebarCollapsed = ref(persisted.sidebarCollapsed ?? false)
	const theme = ref<ThemePreference>(persisted.theme ?? 'system')
	const priceMode = ref<PriceMode>(persisted.priceMode ?? 'INTERNE')

	const showsInternalPrices = computed(() => priceMode.value === 'INTERNE')

	function toggleSidebar(): void {
		sidebarCollapsed.value = !sidebarCollapsed.value
	}

	function togglePriceMode(): void {
		priceMode.value = priceMode.value === 'INTERNE' ? 'ZAKAZNICKE' : 'INTERNE'
	}

	/** Zapíše `data-theme` na `<html>` — tokeny si dark variant prepnú samy. */
	function applyTheme(): void {
		if (typeof document === 'undefined') {
			return
		}

		const prefersDark = globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches === true
		const effective = theme.value === 'system' ? (prefersDark ? 'dark' : 'light') : theme.value
		document.documentElement.setAttribute('data-theme', effective)
	}

	watch(
		[sidebarCollapsed, theme, priceMode],
		() => {
			applyTheme()
			try {
				globalThis.localStorage?.setItem(
					STORAGE_KEY,
					JSON.stringify({
						sidebarCollapsed: sidebarCollapsed.value,
						theme: theme.value,
						priceMode: priceMode.value,
					} satisfies PersistedUiState),
				)
			} catch {
				// Súkromný režim prehliadača — beží sa ďalej bez perzistencie.
			}
		},
		{ immediate: true },
	)

	return { sidebarCollapsed, theme, priceMode, showsInternalPrices, toggleSidebar, togglePriceMode, applyTheme }
})
